import { forwardRef, type HTMLAttributes } from 'react';
import { cx } from '@linaria/atomic';
import { NakedButton } from '../../naked/naked-button.component';
import { Avatar, type AvatarProps } from '../avatar/avatar.component';
import { type SizeProps } from '../avatar/avatar.styles';
import {
  centerOverflowAvatarStyles,
  groupContainerStyles,
  itemAvatarStyles,
  itemWrapperStyles,
  overflowItemWrapperStyles,
} from './circular-avatar-group.styles';

const MAX_VISIBLE_AVATARS_LENGTH = 4;

const getChildSize = (parentSize: SizeProps, count: number): SizeProps => {
  const sizes: SizeProps[] = ['xxs', 'xs', 'small', 'medium', 'large', 'xl', 'xxl'];
  const currentIndex = sizes.indexOf(parentSize);

  // 2 avatars step down by 1 size, 3-4 by 2, and overflow (5+) by 3.
  const stepDown = count === 2 ? 1 : count > MAX_VISIBLE_AVATARS_LENGTH ? 3 : 2;
  const childIndex = Math.max(0, currentIndex - stepDown);

  return sizes[childIndex] as SizeProps;
};

const getRecipientLabel = ({ firstName, lastName, name, title }: AvatarProps) => {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  return name || title || 'Unnamed recipient';
};

export interface CircularAvatarGroupProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  avatars: AvatarProps[];
  size?: SizeProps;
  onOverflowClick?: (e: React.MouseEvent<HTMLElement>) => void;
  disableRecipientHoverLabel?: boolean;
}

interface AvatarGroupItemProps {
  recipient: AvatarProps;
  wrapperClassName?: string;
  size: SizeProps;
  disableRecipientHoverLabel?: boolean;
}

const AvatarGroupItem = ({ recipient, wrapperClassName, size, disableRecipientHoverLabel }: AvatarGroupItemProps) => {
  const isInteractive = !!recipient.onClick && !recipient.disableClick;

  return (
    <div className={wrapperClassName}>
      <Avatar
        {...recipient}
        size={size}
        className={itemAvatarStyles(size, isInteractive)}
        disableHoverLabel={disableRecipientHoverLabel ?? recipient.disableHoverLabel}
      />
    </div>
  );
};

export const CircularAvatarGroup = forwardRef<HTMLDivElement, CircularAvatarGroupProps>(
  ({ avatars, size = 'medium', onOverflowClick, disableRecipientHoverLabel, className, ...rest }, ref) => {
    const count = avatars.length;
    const firstRecipient = avatars[0];
    const recipientNames = avatars.map(getRecipientLabel);
    const recipientSummary = recipientNames.slice(0, 4).join(', ');
    const childSize = getChildSize(size, count);

    const groupAriaLabel =
      count <= 0
        ? 'No avatars'
        : count === 1
        ? `${recipientNames[0]} recipient`
        : count > MAX_VISIBLE_AVATARS_LENGTH
        ? `${count} avatars: ${recipientSummary}, and more`
        : `${count} avatars: ${recipientSummary}`;

    const overflowLabel = `View ${count} avatars`;

    return (
      <div
        ref={ref}
        className={cx(groupContainerStyles(size), className)}
        role='group'
        aria-label={groupAriaLabel}
        {...rest}
      >
        {count === 0 && <Avatar size={size} disableHoverLabel={disableRecipientHoverLabel} />}

        {count === 1 && firstRecipient && (
          <Avatar
            {...firstRecipient}
            size={size}
            disableHoverLabel={disableRecipientHoverLabel ?? firstRecipient.disableHoverLabel}
          />
        )}

        {count > 1 &&
          count <= MAX_VISIBLE_AVATARS_LENGTH &&
          avatars.map((recipient, index) => {
            return (
              <AvatarGroupItem
                key={recipient.id || recipient.userId}
                recipient={recipient}
                wrapperClassName={itemWrapperStyles(count, index)}
                size={childSize}
                disableRecipientHoverLabel={disableRecipientHoverLabel}
              />
            );
          })}

        {count > MAX_VISIBLE_AVATARS_LENGTH &&
          avatars.slice(0, MAX_VISIBLE_AVATARS_LENGTH).map((recipient, index) => {
            return (
              <AvatarGroupItem
                key={recipient.id || recipient.userId}
                recipient={recipient}
                wrapperClassName={overflowItemWrapperStyles(index)}
                size={childSize}
                disableRecipientHoverLabel={disableRecipientHoverLabel}
              />
            );
          })}

        {count > MAX_VISIBLE_AVATARS_LENGTH &&
          (onOverflowClick ? (
            <NakedButton
              className={centerOverflowAvatarStyles(size, true)}
              onClick={onOverflowClick}
              aria-label={overflowLabel}
              data-testid='circular-avatar-group-overflow-button'
            >
              {count}
            </NakedButton>
          ) : (
            <div className={centerOverflowAvatarStyles(size, false)} aria-hidden>
              {count}
            </div>
          ))}
      </div>
    );
  }
);

CircularAvatarGroup.displayName = 'CircularAvatarGroup';
